import { createTranslation } from '@choffmeister/i18next'
import { css } from '@emotion/core'
import { useLocation } from '@reach/router'
import { graphql } from 'gatsby'
import parse from 'html-react-parser'
import React from 'react'

import Block from '../components/Block'
import Body from '../components/Body'
import { Button } from '../components/Button'
import CTAMain, { ButtonBlock } from '../components/ctas/CTAMain'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Header from '../components/Header'
import Image from '../components/Image'
import ImageStrip from '../components/ImageStrip'
import { Intro } from '../components/Intro'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import SEO from '../components/SEO'
import TestimonialCard from '../components/TestimonialCard'
import TextCard from '../components/TextCard'
import { useInstantTourLinkFactory } from '../components/useTracking'
import Video from '../components/Video'
import { breakpoints } from '../constants/breakPoints'
import { FEATURES_CARDS } from '../constants/features'
import { headerHeight } from '../constants/header'
import { getHeroVideo, getOkrVideo, isDesktop } from '../helpers/common'
import { translate } from '../helpers/header'
import { useLanguage } from '../hooks/useLanguage'
import { FeaturePageProps } from '../interfaces/feature'
import common from '../lang/common.json'
import features from '../lang/pages/features.json'

const FeaturePage: React.FunctionComponent<FeaturePageProps> = ({
  data: {
    templatesData: { edges: templates },
  },
  pageContext: {
    slug,
    metaDescription,
    subheading,
    heading,
    subtitle,
    instantTourType,
    instantTourIframeSrc,
    benefits,
    ctaType,
    integrationsTitle,
    integrationsDescription,
    templatesDescription,
    templatesTitle,
    templatesList,
    testimonials,
  },
}) => {
  templates.sort(
    ({ node: { slug: firstSlug } }, { node: { slug: secondSlug } }) =>
      templatesList.indexOf(firstSlug) - templatesList.indexOf(secondSlug)
  )

  const createTourLink = useInstantTourLinkFactory()

  const { useTranslation } = createTranslation({
    common,
    features,
    metaDescription,
    subheading,
    heading,
    subtitle,
    integrationsTitle,
    integrationsDescription,
    templatesDescription,
    templatesTitle,
  })
  const { t } = useTranslation()

  const isEnPage = useLanguage() === 'en'

  const location = useLocation()

  return (
    <Layout whiteBG>
      <SEO lang={useLanguage()} title={t('subheading')} description={t('metaDescription')} />
      <Header />
      <Body>
        {instantTourIframeSrc && isEnPage && isDesktop() ? (
          // show embedded instant tour only for english LP
          <Block columnsTemplate="1.5fr 2fr" css={heroBlockStyle}>
            <div>
              <h1 className="subtitle gray-text">{t('subheading').toUpperCase()}</h1>
              <h2 className="heading2 no-margin">{parse(t('heading'))}</h2>
              <div className="subtitle">{parse(t('subtitle'))}</div>
              <ButtonBlock
                ghostButton={
                  <Button
                    className="black"
                    type="ghost"
                    link={
                      isDesktop() ? `/product/${slug}#feature-instant-tour-iframe` : createTourLink(instantTourType)
                    }
                  >
                    <p className="strong-body-2">{t('common.tourText')}</p>
                  </Button>
                }
              />
            </div>
            <Video
              className="gray-bordered-block"
              srcType={[
                { src: getHeroVideo(slug).MP4, type: 'video/mp4' },
                { src: getHeroVideo(slug).Webm, type: 'video/webm' },
              ]}
              ratio={1.756363636}
              playInView={isDesktop(breakpoints.large) ? false : true}
            />
          </Block>
        ) : (
          <Intro
            subHeading={t('subheading')}
            heading={t('heading')}
            subtitle={t('subtitle')}
            imgSrc={slug === 'capacity-planning' ? 'features/capacity-planning/capacity-planning.webp' : undefined}
            heroVideo={
              slug === 'capacity-planning' ? undefined : (
                <Video
                  className="gray-bordered-block"
                  srcType={[
                    { src: getHeroVideo(slug).MP4, type: 'video/mp4' },
                    { src: getHeroVideo(slug).Webm, type: 'video/webm' },
                  ]}
                  ratio={1.756363636}
                  playInView={isDesktop(breakpoints.large) ? false : true}
                />
              )
            }
            withInstantTourButton
            instantTourType={instantTourType}
            customBreakPoint={breakpoints.large}
            cssStyles={heroIntroStyle(slug)}
          />
        )}

        {instantTourIframeSrc && isEnPage && isDesktop() && (
          // show embedded instant tour only for english LP
          <div id="feature-instant-tour-iframe" css={instantTourAnchorStyle}>
            <div className="gray-bordered-block with-blue-box-shadow" css={instantTourFrameStyle}>
              <iframe
                src={instantTourIframeSrc}
                style={{ border: 'none', width: '100%', height: '100%' }}
                allow="fullscreen"
              />
            </div>
          </div>
        )}

        <Divider size={8} color={isDesktop() && instantTourIframeSrc && isEnPage ? 'transparent' : '#efeff0'} />

        {benefits.map(({ title, description, videoFileName, imageSrc, imgStyle, imageCss, withBorder }, index) => {
          const mediaOnLeft = (index + 1) % 2 === 0
          const { useTranslation } = createTranslation({ title, description })
          const { t } = useTranslation()
          return (
            <React.Fragment key={`feature-block-${index}`}>
              <Block
                columns={2}
                columnsTemplate={mediaOnLeft ? '2fr 1fr' : '1fr 2fr'}
                columnGap={2}
                responsiveReversed={mediaOnLeft}
                key={`chart-block-item-${index}`}
              >
                {!mediaOnLeft && (
                  <div>
                    <h3 className="heading3">{parse(t('title'))}</h3>
                    <div className="body-1">{parse(t('description'))}</div>
                  </div>
                )}
                {(imageSrc || videoFileName) &&
                  (videoFileName ? (
                    <Video
                      srcType={[
                        { src: getOkrVideo(videoFileName).MP4, type: 'video/mp4' },
                        { src: getOkrVideo(videoFileName).Webm, type: 'video/webm' },
                      ]}
                      ratio={1.756363636}
                      playInView
                      className={withBorder ? 'gray-bordered-block' : ''}
                      loopDelay={4000}
                    />
                  ) : (
                    imageSrc && (
                      <div>
                        <Image
                          src={imageSrc}
                          alt={t('title')}
                          imgStyle={imgStyle}
                          css={[imageCss, !withBorder ? blockImageStyle : undefined]}
                          className={withBorder ? 'gray-bordered-block' : undefined}
                        />
                      </div>
                    )
                  ))}
                {mediaOnLeft && (
                  <div>
                    <h3 className="heading3">{parse(t('title'))}</h3>
                    <div className="body-1">{parse(t('description'))}</div>
                  </div>
                )}
              </Block>
              {index !== benefits.length - 1 && <Divider size={8} />}
            </React.Fragment>
          )
        })}

        {slug !== 'objectives-okrs' && slug !== 'capacity-planning' ? (
          <>
            <CTAMain type={ctaType} cssStyles={ctaStyle} />

            <ImageStrip
              heading={t('integrationsTitle')}
              subtitle={t('integrationsDescription')}
              linkText={t('features.integrationsBlock.link.text')}
              linkUrl="/integrations/"
            />

            {isEnPage && (
              <>
                <Divider size={6} color="transparent" />

                <div css={templatesInfoStyle}>
                  <h2 className="heading2 center" css={templatesTitleStyle}>
                    {parse(t('templatesTitle'))}
                  </h2>
                  <div className="body-1 center">{parse(t('templatesDescription'))}</div>
                  <Link className="body-1 blue-text center" href="/templates/" withArrow css={templatesLinkStyle}>
                    {t('features.templatesBlock.link.text')}
                  </Link>
                </div>
                <Block columns={3} noBreakForTablet>
                  {templates.map(
                    ({
                      node: {
                        name,
                        slug,
                        visual,
                        abstract: { abstract },
                      },
                    }) => (
                      <TextCard
                        title={name}
                        description={abstract}
                        linkText="Try it out"
                        linkUrl={`/templates/${slug}/`}
                        gatsbyImageSrc={visual}
                        key={slug}
                      />
                    )
                  )}
                </Block>
              </>
            )}
          </>
        ) : (
          <Divider size={4} color="transparent" />
        )}

        <TestimonialCard
          testimonialData={testimonials}
          cssStyles={slug === 'prioritization' ? testimonialStyle : css``}
        />

        <Divider size={4} color="transparent" />

        <h2 className="heading2 center">{t('features.featuresBlock.heading')}</h2>
        <Block columns={4} twoColumnsForTablet>
          {FEATURES_CARDS.filter(({ link }) => !location.pathname.includes(translate(link)))
            .slice(0, 4)
            .map(({ title, description, link, imageSrc }) => (
              <TextCard
                key={translate(title)}
                title={translate(title)}
                description={description}
                linkText={t('common.learnMoreText')}
                linkUrl={translate(link)}
                imageSrc={imageSrc}
              />
            ))}
        </Block>

        <Divider size={4} color="transparent" />

        <CTAMain type={slug === 'objectives-okrs' || slug === 'capacity-planning' ? ctaType : 'general'} />
      </Body>
      <Footer />
    </Layout>
  )
}

export default FeaturePage

export const query = graphql`
  query ($templatesFilter: [String]) {
    templatesData: allContentfulProductTemplate(filter: { slug: { in: $templatesFilter } }) {
      edges {
        node {
          name
          abstract {
            abstract
          }
          slug
          visual {
            gatsbyImageData(width: 600, quality: 90)
            title
          }
        }
      }
    }
  }
`

const heroBlockStyle = css`
  margin: 4rem 0 8rem;
`

// export for AdPageTemplate.tsx
export const heroIntroStyle = (slug: string) => css`
  ${slug === 'objectives-okrs' && 'grid-template-columns: 470px auto'};
  ${slug === 'capacity-planning' &&
  `
    .gatsby-image-wrapper {
      @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.large}px) {
        width: 32rem;
        margin: 2rem auto 0;
      }
    }
  `};

  column-gap: 1rem;

  .gray-bordered-block {
    @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${breakpoints.large}px) {
      margin: 2rem 7% 0;
    }

    @media screen and (max-width: ${breakpoints.large}px) {
      margin-top: 2rem;
    }
  }
`

const instantTourAnchorStyle = css`
  padding-top: 6.5rem !important;
  margin-top: -6.5rem !important;
`

const instantTourFrameStyle = css`
  height: calc(100vh - ${headerHeight}px - 4rem);
  max-height: 1050px;
`

const templatesTitleStyle = css`
  margin-bottom: 1rem !important;
`

const templatesLinkStyle = css`
  margin: 1rem auto 4rem !important;
  display: block;
`

const templatesInfoStyle = css`
  @media screen and (max-width: ${breakpoints.small}px) {
    * {
      text-align: left !important;
    }
  }
`

const testimonialStyle = css`
  @media screen and (max-width: ${breakpoints.small}px) {
    .blue-gradient-bg {
      min-height: 40.5rem;
    }
  }
`

export const ctaStyle = css`
  margin: 12rem 0;

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 10rem 0;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 8rem 0 6rem;
  }
`

// export for Competitor.tsx
export const blockImageStyle = css`
  @media screen and (max-width: ${breakpoints.small}px) {
    height: 50%;
    width: auto;
  }
`
